// Reusable "how do I actually click this" micro-guides for the tool actions
// that come up over and over across lessons and hackathons (Excel Online,
// Tableau Public). Written once here, then linked inline from lesson/task
// markdown with `[[howto:guideId|link text]]` (parsed by MarkdownRenderer's
// InlineText and rendered via <HowToLink>) instead of re-explaining the same
// click path in every lesson that needs it.
//
// Keep each guide to the exact clicks — no theory, no "why" (that belongs in
// the lesson concept text). If a step depends on something the learner set
// up earlier (a specific sheet/column name), say so in the step rather than
// assuming it.

const HOW_TO_GUIDES = {
  'github-clone-repo': {
    tool: 'GitHub + VS Code',
    title: 'Clone a GitHub repo into VS Code',
    steps: [
      'Open the repo on GitHub and click the green Code button.',
      'Copy the HTTPS URL.',
      'Open VS Code, then open Terminal -> New Terminal.',
      'Run git clone followed by the copied URL.',
      'Run cd followed by the new folder name, or use File -> Open Folder to open it in VS Code.',
    ],
    tip: 'Clone only once. After that, open the existing folder and use git pull to get updates.',
  },
  'github-branch-pr': {
    tool: 'GitHub + Git',
    title: 'Create a branch and open a pull request',
    steps: [
      'In your repo terminal, run git pull while you are on main so you start from the latest version.',
      'Run git checkout -b followed by a short branch name.',
      'Make your change and save the files.',
      'Run git add ., then git commit -m "clear message".',
      'Run git push -u origin followed by your branch name.',
      'Open GitHub and click Compare & pull request, then describe what changed and create the PR.',
    ],
    tip: 'For shared cohort work, pull before you start and again before your final push so you reduce conflicts.',
  },
  'github-commit-push': {
    tool: 'Git + GitHub',
    title: 'Commit and push your work',
    steps: [
      'Run git status to see changed files.',
      'Run git add followed by the files you want to include, or git add . for everything in the current repo.',
      'Run git commit -m "short message explaining the change".',
      'Run git pull to bring in remote changes before pushing.',
      'Run git push.',
    ],
    tip: 'A good commit message says what changed, not just "update".',
  },
  'github-pat-settings': {
    tool: 'GitHub',
    title: 'Create a GitHub personal access token',
    steps: [
      'On GitHub, click your profile photo, then Settings.',
      'Open Developer settings, then Personal access tokens.',
      'Create a fine-grained token for your repo.',
      'Give it Contents read/write access so Qode Clarity can update your journal files.',
      'Copy the token immediately and paste it into Qode Clarity settings.',
    ],
    tip: 'Treat the token like a password. If you expose it, delete it in GitHub and create a new one.',
  },
  'excel-insert-chart': {
    tool: 'Excel Online',
    title: 'Insert a clustered bar chart',
    steps: [
      'Select the columns you want to chart, including their header row (e.g. click the header of the first column, then shift-click the header of the last one).',
      'Go to the Insert tab.',
      'In the Charts group, click the bar chart icon, then choose Clustered Bar (or Clustered Column for vertical bars).',
      'The chart appears on the sheet — drag it by its edge to reposition, or drag a corner handle to resize.',
      'Click the chart title text and retype it to something specific (e.g. what the chart shows and why it matters), not the default "Chart Title".',
    ],
    tip: 'If nothing happens when you click the chart icon, check that your selection includes at least two columns and a header row — Excel needs both to know what to chart.',
  },
  'excel-format-data-point': {
    tool: 'Excel Online',
    title: "Change one bar or point's colour",
    steps: [
      'Click once on the chart to select it, then click again directly on the single bar/point you want to change (a single click on the series selects all bars; a second click on just one bar selects only that one).',
      'Right-click the selected bar and choose Format Data Point from the menu.',
      'In the panel that opens on the right, open Fill, choose Solid fill, then pick your colour.',
      'Click outside the chart to deselect and see the result.',
    ],
    tip: 'If the whole series changes colour instead of just one bar, you only did the first click — click a second time on that specific bar before right-clicking.',
  },
  'excel-chart-reference-line': {
    tool: 'Excel Online',
    title: 'Add a horizontal reference/target line to a chart',
    steps: [
      'Add a new column to your data with the target value repeated on every row (e.g. a "Target 100%" column filled with 100 on each row) — a chart series needs one value per row, it cannot draw a single floating line from one number.',
      'Click the chart, then right-click it and choose Select Data.',
      'Click Add, set the series name (e.g. "Target"), and set the values to your new target column, then click OK.',
      'Right-click the new target series in the chart and choose Change Series Chart Type.',
      'Change just that series to Line, and click OK — it now overlays as a flat reference line across the other bars.',
    ],
    tip: 'This is the standard workaround in Excel — there is no built-in "add a flat line" button, so a repeated-value helper column is the normal way to do it. Use this method — it is the one that reliably works in Excel Online.',
    advanced: {
      label: 'Advanced (optional, may not work): scatter series overlay',
      note: 'On desktop Excel, the same result can be done without a helper column: add an XY (Scatter) series with just two points — (first x, target) and (last x, target) — and Excel draws one straight line spanning the whole chart. This depends on changing one series in a combo chart to XY Scatter type, which desktop Excel supports but Excel Online often does not expose. If a task calls this out as "advanced" or "if possible," treat it as optional — do not spend time hunting for a Scatter option in Change Series Chart Type that may not be there. The helper-column method above produces the same visible result and is guaranteed to work in Excel Online.',
    },
  },
  'excel-chart-data-labels-gridlines': {
    tool: 'Excel Online',
    title: 'Clean up chart gridlines and data labels',
    steps: [
      'Click the chart once so the chart border appears.',
      'To remove gridlines: click one gridline inside the chart area, then press Delete. If that does not work, use Chart → Chart Elements and untick Gridlines.',
      'To add data labels: click the chart, open Chart Elements, then tick Data Labels.',
      'If labels appear on every series but the task asks for Actual only, click one label from the unwanted series and press Delete, or right-click that series and turn its data labels off.',
      'Check the chart again: keep only the labels that help the reader see the decision quickly.',
    ],
    tip: 'Do not label everything if it makes the chart noisy. For learner tasks, it is fine to label only the series the question asks for.',
  },
  'excel-worksheet-gridlines': {
    tool: 'Excel Online',
    title: 'Hide worksheet gridlines',
    steps: [
      'Click the worksheet tab you want to clean up, e.g. Dashboard.',
      'Go to the View tab.',
      'Find the Show section.',
      'Untick Gridlines. This hides the grey cell lines on the current worksheet.',
      'Check that the Dashboard now has a clean white background. If other sheets still show gridlines, that is fine; this setting is per sheet.',
    ],
    tip: 'Worksheet gridlines are different from chart gridlines. Use View -> Gridlines for the sheet background; click inside a chart only when you want to remove lines inside the chart itself.',
  },
  'excel-chart-title': {
    tool: 'Excel Online',
    title: 'Edit a chart title',
    steps: [
      'Click the chart title text once.',
      'Click inside the title again so the text cursor appears.',
      'Replace the default title with a sentence that says what the chart shows.',
      'Press Enter or click outside the chart.',
    ],
    tip: 'A useful title sounds like an insight, not a file name. Example: "Line E needs urgent attention — 20% below daily target".',
  },
  'excel-sort-table': {
    tool: 'Excel Online',
    title: 'Sort a table or range',
    steps: [
      'Click any cell in the column you want to sort by.',
      'Go to the Data tab.',
      'Choose Sort Ascending or Sort Descending. For numbers, descending means largest first.',
      'If Excel asks whether to expand the selection, choose Expand the selection so the full rows stay together.',
      'Check the first few rows to confirm the whole table moved together, not just one column.',
    ],
    tip: 'If only one column changed and the row values no longer line up, undo immediately. Sorting must move full rows together.',
  },
  'excel-filter-table': {
    tool: 'Excel Online',
    title: 'Filter a table or range',
    steps: [
      'Click inside the data table.',
      'Go to the Data tab and choose Filter if filter arrows are not already visible.',
      'Open the dropdown arrow on the column you want to filter.',
      'Untick Select All, then tick only the values you need, or use Number/Text Filters for conditions.',
      'Check the visible row count and clear the filter when you are finished.',
    ],
    tip: 'Filtering hides rows; it does not delete them. If the row numbers skip, a filter is active.',
  },
  'excel-pivot-table': {
    tool: 'Excel Online',
    title: 'Build a PivotTable',
    steps: [
      'Click any cell inside your data table.',
      'Go to the Insert tab, then click PivotTable.',
      'Choose New Worksheet (keeps it separate from your raw data), then click OK/Create.',
      'The PivotTable Fields panel opens on the right — drag the field you want to group by into Rows.',
      'Drag the field you want to group by a second time (e.g. Month) into Columns, if you need a two-way breakdown.',
      'Drag the number field you want to summarise into Values — it defaults to Sum, which is usually what you want for totals.',
    ],
    tip: 'If a Values field shows a Count instead of a Sum, that column likely has some blank or text cells in it — Excel falls back to counting when it cannot sum everything.',
  },
  'excel-pivot-slicer': {
    tool: 'Excel Online',
    title: 'Add and move slicers for a PivotTable',
    steps: [
      'Go to the sheet that contains the PivotTable, e.g. PivotAnalysis or PivotTable. Click one cell inside the PivotTable numbers or row labels. Do not click a blank cell outside it.',
      'Check that the PivotTable Fields panel appears on the right. If it does not, click inside the PivotTable again. This confirms Excel knows which PivotTable you are editing.',
      'Open the PivotTable Analyze tab. In some Excel versions it is called PivotTable Analyse. This tab only appears after a PivotTable cell is selected.',
      'Click Insert Slicer. If you cannot see it, widen the browser window or look in the Filter group on the PivotTable Analyze tab.',
      'In the Insert Slicers box, tick RoomType and Month for the hotel task. The lesson says Quarter, but the sample data uses month names, so Month is the available field to use.',
      'Click OK. Excel creates two floating slicer boxes. They may appear on top of the PivotTable; that is normal.',
      'Test before moving them: click Suite in the RoomType slicer. The PivotTable and linked PivotChart should filter to Suite only. Click the small clear-filter icon in the slicer header to show all rooms again.',
      'Move the slicers to the Dashboard sheet: click the border/header area of one slicer so the whole slicer is selected, press Ctrl+X, click the Dashboard sheet tab, then press Ctrl+V.',
      'Drag the slicer above the chart. Repeat cut/paste for the second slicer. Keep both slicers above or beside the chart, not covering the chart.',
      'If you have more than one PivotTable, connect the slicers: right-click a slicer, choose Report Connections, tick every PivotTable that should respond, then click OK.',
      'Final check: click Deluxe, Suite, or a month in the slicer. The visible PivotTable numbers and chart should change. Clear the slicer filter before continuing if the next task needs all data.',
    ],
    tip: 'If Insert Slicer is greyed out or missing, you are probably not clicked inside a PivotTable. Click directly on a Row Labels or value cell first, then reopen PivotTable Analyze.',
    advanced: {
      label: 'Common mistake in this hotel task',
      note: 'The task says RoomType and Quarter, but the provided sample data has Month, not Quarter. Use RoomType and Month unless you created a separate Quarter field yourself.',
    },
  },
  'excel-pivot-report-connections': {
    tool: 'Excel Online',
    title: 'Connect slicers to multiple PivotTables',
    steps: [
      'Start only after you have at least one slicer on the sheet. In the hotel task, use the RoomType slicer or the Month slicer.',
      'Click the slicer once so its border and resize handles appear. This selects the slicer itself, not a cell behind it.',
      'Right-click the selected slicer border or slicer header, then choose Report Connections. In some Excel versions this may be called PivotTable Connections.',
      'A dialog opens with a list of PivotTables in the workbook. Tick every PivotTable that this slicer should control.',
      'Click OK.',
      'Repeat the same steps for the second slicer, e.g. Month. Each slicer has its own Report Connections setting.',
      'Test it: click Suite in the RoomType slicer. Every connected PivotTable and PivotChart should change together.',
      'If only one PivotTable changes, right-click the slicer again, reopen Report Connections, and tick the missing PivotTable.',
      'If the list shows only one PivotTable, either you have not created the second PivotTable yet, or the second PivotTable was built from a different source/range.',
      'If you only have one PivotTable right now, skip this task and return after creating the second PivotTable.',
    ],
    tip: 'Report Connections does not create a slicer. It tells an existing slicer which PivotTables it should control.',
    advanced: {
      label: 'Common naming issue',
      note: 'Excel may show PivotTable names like PivotTable1 and PivotTable2 instead of friendly names. Tick them, click OK, then test the slicer to confirm which visuals respond.',
    },
  },
  'excel-pivot-refresh': {
    tool: 'Excel Online',
    title: 'Refresh a PivotTable after data changes',
    steps: [
      'Click inside the PivotTable.',
      'Go to the PivotTable Analyze tab.',
      'Click Refresh.',
      'Check that the row totals or counts now match the latest source data.',
      'If new rows are still missing, check that the source data is an Excel Table, not a fixed range that excludes new rows.',
    ],
    tip: 'A PivotTable does not always update automatically after source data changes. Refresh before trusting it.',
  },
  'excel-vlookup': {
    tool: 'Excel Online',
    title: 'Use VLOOKUP to pull data from another table',
    steps: [
      'Click the cell where you want the looked-up value to appear.',
      'Type =VLOOKUP( and click the cell containing the value you want to look up (e.g. a Region code).',
      'Type a comma, then select the entire lookup table (the table that contains both the value you searched for and the value you want back).',
      'Type a comma, then type the column number inside that table that holds the value you want returned, counting the first column of your selection as 1.',
      'Type a comma, then type FALSE for an exact match (almost always what you want) — then close the bracket and press Enter.',
    ],
    tip: 'If VLOOKUP returns #N/A, the value in your first cell almost never matches the lookup table exactly — check for extra spaces or mismatched capitalisation/text-vs-number formatting.',
  },
  'excel-xlookup': {
    tool: 'Excel Online',
    title: 'Use XLOOKUP (the modern lookup function)',
    steps: [
      'Click the cell where you want the looked-up value to appear.',
      'Type =XLOOKUP( and click the cell containing the value you want to look up.',
      'Type a comma, then select just the column of values you are searching within (not the whole table).',
      'Type a comma, then select just the column you want the result to come from (it must have the same number of rows as the column you searched).',
      'Type a comma, then type a fallback value in quotes (e.g. "Not found") so a missing match shows a clear message instead of an error — then close the bracket and press Enter.',
    ],
    tip: 'Unlike VLOOKUP, XLOOKUP does not care which column is to the left of which, and it keeps working even if you insert new columns into the lookup table later.',
  },
  'excel-conditional-formatting': {
    tool: 'Excel Online',
    title: 'Highlight cells with Conditional Formatting',
    steps: [
      'Select the range of cells you want to check (not the whole sheet — just the cells the rule should apply to).',
      'Go to the Home tab, then click Conditional Formatting.',
      'Choose Highlight Cells Rules, then pick the comparison you need (e.g. Greater Than, Less Than, Between).',
      'Type the value to compare against, choose a fill colour from the dropdown, then click OK.',
    ],
    tip: 'Rules only apply to the exact range you had selected when you created them — if new rows do not get highlighted, the rule range needs extending (Conditional Formatting → Manage Rules → edit the "Applies to" box).',
  },
  'excel-index-match': {
    tool: 'Excel Online',
    title: 'Use INDEX/MATCH for a flexible lookup',
    steps: [
      'Click the cell where you want the result to appear.',
      'Type =INDEX( then select the column you want the answer to come from, and type a comma.',
      'Type MATCH( then click the cell containing the value you want to find, type a comma, select the column to search within, type another comma, then type 0 for an exact match, and close that inner bracket.',
      'Close the outer bracket and press Enter.',
    ],
    tip: 'INDEX/MATCH does the same job as VLOOKUP but can look to the left as well as the right of the search column — reach for it when the answer you need sits before the column you are searching.',
  },
  'tableau-calculated-field': {
    tool: 'Tableau Public',
    title: 'Create a calculated field',
    steps: [
      'In the Data pane on the left, click the small dropdown arrow at the top and choose Create Calculated Field (or right-click any existing field and choose Create → Calculated Field).',
      'Give the field a clear, specific name (e.g. "Profit Margin", not "Calc1").',
      'Write the formula in the box — start typing a field name and Tableau will autocomplete it from your data.',
      'Click OK. The new field appears in the Data pane and can be dragged onto any shelf like a normal field.',
    ],
  },
  'tableau-shelf-basics': {
    tool: 'Tableau Public',
    title: 'Build a basic chart with the Rows/Columns shelves',
    steps: [
      'Drag the field you want along the bottom axis onto the Columns shelf at the top of the worksheet.',
      'Drag the field you want along the side axis onto the Rows shelf.',
      'Drag a number field onto the Marks card\'s Color, Size, or Label boxes to encode it visually instead of as another axis.',
      'Tableau picks a default chart type automatically based on what you dropped where — use the Show Me panel (top right) to switch to a specific chart type once your fields are placed.',
    ],
  },
  'python-create-venv': {
    tool: 'Python',
    title: 'Create and activate a virtual environment',
    steps: [
      'Open a terminal in your project folder.',
      'Run python -m venv .venv.',
      'On Windows PowerShell, run .venv\\Scripts\\Activate.ps1. On Git Bash, run source .venv/Scripts/activate.',
      'Confirm the terminal prompt shows (.venv).',
      'Install packages only after the environment is active.',
    ],
    tip: 'One project should have one virtual environment. Do not install every project into the global Python.',
  },
  'python-pip-install': {
    tool: 'Python',
    title: 'Install packages with pip',
    steps: [
      'Activate your virtual environment first.',
      'Run python -m pip install followed by the package name.',
      'If the project has requirements.txt, run python -m pip install -r requirements.txt.',
      'Run python -m pip freeze to check what is installed.',
      'If you need to save dependencies, run python -m pip freeze > requirements.txt.',
    ],
    tip: 'Use python -m pip instead of plain pip so the package installs into the Python environment you are actually using.',
  },
  'python-run-pytest': {
    tool: 'Python',
    title: 'Run pytest',
    steps: [
      'Activate your virtual environment.',
      'Install pytest if needed: python -m pip install pytest.',
      'From the project root, run python -m pytest.',
      'To run one test file, add the file path after the command.',
      'Read the first failing test carefully before changing code.',
    ],
    tip: 'If pytest says no tests were found, check that files are named like test_something.py or something_test.py.',
  },
  'python-jupyter-notebook': {
    tool: 'Jupyter',
    title: 'Open a notebook',
    steps: [
      'Activate your virtual environment.',
      'Install notebook support if needed: python -m pip install notebook ipykernel.',
      'Run python -m notebook or open the .ipynb file in VS Code.',
      'Select the correct kernel, usually the .venv environment for the project.',
      'Run cells from top to bottom so variables are created in order.',
    ],
    tip: 'If a cell says a variable is undefined, restart the kernel and run the notebook from the first cell.',
  },
  'pandas-read-csv': {
    tool: 'Python / pandas',
    title: 'Load a CSV with pandas',
    steps: [
      'Import pandas: import pandas as pd.',
      'Use df = pd.read_csv("path/to/file.csv").',
      'Run df.head() to inspect the first rows.',
      'Run df.info() to check column names and data types.',
      'If the file is not found, check your current working directory or use the full relative path.',
    ],
    tip: 'Most pandas errors here are path errors. Print the file path or use pathlib to confirm the file exists.',
  },
  'pandas-merge': {
    tool: 'Python / pandas',
    title: 'Join two DataFrames with merge',
    steps: [
      'Identify the key column that exists in both DataFrames.',
      'Use result = left.merge(right, on="key_column", how="left").',
      'Check result.shape before and after the merge.',
      'Check for missing values in the newly joined columns.',
      'If rows multiplied unexpectedly, check for duplicate keys in the right-hand DataFrame.',
    ],
    tip: 'A merge is like a lookup. Duplicate keys can create extra rows, so always check row count after merging.',
  },
  'pandas-groupby': {
    tool: 'Python / pandas',
    title: 'Summarise data with groupby',
    steps: [
      'Choose the column that defines the groups.',
      'Choose the numeric column to summarise.',
      'Use df.groupby("group_col")["value_col"].sum().reset_index().',
      'Sort the result if you need a ranking.',
      'Rename columns so the output is readable.',
    ],
    tip: 'Groupby answers "total/average/count by category". If you need more than one metric, use .agg().',
  },
  'python-matplotlib-chart': {
    tool: 'Python / matplotlib',
    title: 'Create a simple matplotlib chart',
    steps: [
      'Import matplotlib.pyplot as plt.',
      'Prepare the grouped or cleaned data first.',
      'Use plt.bar(...) for category comparison or plt.plot(...) for a trend.',
      'Add a title and axis labels.',
      'Use plt.tight_layout(), then plt.show() in a notebook or plt.savefig("chart.png") for evidence.',
    ],
    tip: 'Do the calculation before charting. A chart should show a clean result, not raw confusion.',
  },
  'azure-create-resource-group': {
    tool: 'Azure Portal',
    title: 'Create a resource group',
    steps: [
      'Open the Azure Portal and search for Resource groups.',
      'Click Create.',
      'Choose the subscription and region given by your lab or mentor.',
      'Use a clear name, such as rg-qode-week-02-yourname.',
      'Review and create.',
    ],
    tip: 'A resource group is the folder for your Azure resources. Put related lab resources together so cleanup is easy.',
  },
  'azure-storage-container': {
    tool: 'Azure Portal',
    title: 'Create a storage account and blob container',
    steps: [
      'Create or open a Storage account in your lab resource group.',
      'Use standard settings unless the lesson specifies otherwise.',
      'Open Containers under Data storage.',
      'Click + Container and give it a simple lowercase name.',
      'Upload the dataset or test file into the container.',
    ],
    tip: 'Storage account names must be globally unique and lowercase. Container names are also lowercase.',
  },
  'azure-adf-pipeline': {
    tool: 'Azure Data Factory',
    title: 'Create a basic ADF pipeline',
    steps: [
      'Open your Data Factory and click Launch Studio.',
      'Go to Author, then click + and choose Pipeline.',
      'Add the activity required by the lesson, often Copy Data.',
      'Configure source and sink datasets.',
      'Click Debug to test, then Publish when the pipeline works.',
    ],
    tip: 'Debug tests your draft. Publish saves the pipeline so it exists outside your current editing session.',
  },
  'azure-key-vault-secret': {
    tool: 'Azure Portal',
    title: 'Add a secret to Key Vault',
    steps: [
      'Open or create a Key Vault in your resource group.',
      'Open Secrets, then click Generate/Import.',
      'Give the secret a clear name.',
      'Paste the secret value and create it.',
      'Grant only the required identity or user permission to read the secret.',
    ],
    tip: 'Never paste real secrets into screenshots, GitHub, or journal text. Describe the secret name, not the value.',
  },
  'databricks-notebook-cluster': {
    tool: 'Databricks',
    title: 'Run a Databricks notebook on a cluster',
    steps: [
      'Open the Databricks workspace.',
      'Create or open a notebook.',
      'Attach the notebook to an existing cluster, or create a small lab cluster if instructed.',
      'Run cells from top to bottom.',
      'Save the notebook link, exported notebook, or run output as evidence.',
    ],
    tip: 'If a cell will not run, check that the notebook is attached to a running cluster.',
  },
  'fabric-notebook-lakehouse': {
    tool: 'Microsoft Fabric',
    title: 'Attach a notebook to a lakehouse',
    steps: [
      'Open the Fabric workspace for the lab.',
      'Create or open the lakehouse.',
      'Create a notebook or open the provided notebook.',
      'Attach the lakehouse to the notebook from the Explorer/Lakehouse panel.',
      'Run a small read command to confirm the notebook can see the lakehouse files or tables.',
    ],
    tip: 'If the notebook cannot see data, check that the correct lakehouse is attached, not just open in another tab.',
  },
};

export function getHowToGuide(guideId) {
  if (!guideId) return null;
  return HOW_TO_GUIDES[String(guideId).trim()] || null;
}

export default HOW_TO_GUIDES;
